import {Injectable} from "@angular/core";
import {
  AlgemeneInformatie,
  CasaClementina,
  ClientFiche,
  ClientFicheDienst,
  Knelpunten,
  MogelijkeProblematieken,
  Takenpakket,
  TakenpakketCategorie
} from "../../client-fiche";

const pdfStylesheet = {
  title: {
    fontSize: 20,
  },
  subheader: {
    fontSize: 18,
    margin: [0, 16, 0, 0],
    decoration: 'underline',
  },
  cardheader: {
    fontSize: 14,
    margin: [0, 12, 0, 4],
    bold: true,
  },
  cardcontentsubheader: {
    fontSize: 12,
    color: '#00008B',
  },
  margintop10: {
    margin: [0, 10, 0, 0],
  },
  cardcontent: {
    fontSize: 12,
  },
};


@Injectable({
  providedIn: 'root'
})
export class ClientFichePdfService {
  public generateVolledigeClientFichePDF(pdfTitle: string, clientFiche: ClientFiche) {
    return {
      info: {
        title: pdfTitle,
      },
      content: [
        {text: pdfTitle, style: 'title'},
        {stack: this.mapAlgemeneInformatie(clientFiche.getAlgemeneInformatie())},
        {stack: this.mapTakenPakket(clientFiche.getTakenpakket())},
        {stack: this.mapKnelpunten(clientFiche.getKnelpunten())},
        {stack: this.mapMogelijkeProblematieken(clientFiche.getMogelijkeProblematieken())},
        {stack: this.mapAndereDiensten(clientFiche.getAndereDiensten() || [])},
      ],
      styles: pdfStylesheet
    };
  }

  generateClientGerichtePDF(pdfTitle: string, clientFiche: ClientFiche) {
    return {
      info: {
        title: pdfTitle,
      },
      content: [
        {text: pdfTitle, style: 'title'},
        {stack: this.mapClientGerichteTakenPakket(clientFiche.getTakenpakket())},
      ],
      styles: pdfStylesheet
    };
  }

  private mapAlgemeneInformatie(algemeneInformatie: AlgemeneInformatie | undefined) {
    if (algemeneInformatie) {
      return [{text: 'Algemene informatie', style: 'subheader'},
        {
          columns: [
            this.mapToCard('Dienstencheques',
              [algemeneInformatie.getDienstencheques(),
                algemeneInformatie.getGebruikersnummer() ? 'Met gebruikersnummer: ' + algemeneInformatie.getGebruikersnummer() : undefined]
            ),
            this.mapToCard('Geboortedatum',
              [algemeneInformatie.getGeboortedatum()]
            ),
          ].filter(column => !!column)
        },
        {
          columns: [
            this.mapToCard('Moedertaal',
              [algemeneInformatie.getMoedertaal()]
            ),
            this.mapToCard('Taal waarin je met cliënt kan praten',
              [algemeneInformatie.getConversatieTaal()]
            ),
          ].filter(column => !!column)
        },
        {
          columns: [
            this.mapToCard('Key-lock code',
              [algemeneInformatie.getKeyLock()]
            ),
            this.mapToCard('Autostaanplaats / Parking in de buurt',
              [algemeneInformatie.getAutoStaanPlaats()]
            ),
          ].filter(column => !!column)
        },
        this.mapToCard('Extra info i.v.m. bereikbaarheid',
          [algemeneInformatie.getExtraInfoBereikbaarheid()]
        ),
        this.mapToCard('Inwonende gezinsleden',
          [algemeneInformatie.getInwonendeGezinsleden()]
        ),
        this.mapToCard('Korte sociale situatieschets',
          [algemeneInformatie.getSocialeSituatieSchets()]
        ),
        this.mapToCard('Extra informatie/aandachtspunten',
          [algemeneInformatie.getExtraInformatie()]
        ),
      ].filter(line => !!line);
    } else {
      return []
    }
  }

  private mapTakenPakket(takenPakket: Takenpakket | undefined) {
    if (takenPakket) {
      return [{text: 'Takenpakket binnen Zorg Leuven', style: 'subheader'},
        this.mapToCard('Reden van opstart', [takenPakket.getRedenOpstart()]),
        this.mapToCard('Te behalen doelstellingen', [takenPakket.getTeBehalenDoelstellingen()]),
        this.mapToTakenPakketCategorieCard('Poetsdienst', takenPakket.getPoetsdienst()),
        this.mapToTakenPakketCategorieCard('Gezinszorg', takenPakket.getGezinszorg()),
        this.mapToTakenPakketCategorieCard('Maaltijden aan huis', takenPakket.getMaaltijdenAanHuis()),
        this.mapToTakenPakketCategorieCard('Klusjesdienst', takenPakket.getKlusjesdienst()),
        this.mapToTakenPakketCategorieCard('Eerstelijnspsycholoog', takenPakket.getPsycholoog()),
        this.mapToTakenPakketCategorieCard('Ergotherapeut', takenPakket.getErgotherapeut()),
        this.mapToTakenPakketCategorieCard('Mantelzorgcoördinator', takenPakket.getMantelzorgcoordinator()),
        this.mapToTakenPakketCategorieCard('Casemanager', takenPakket.getCasemanager()),
        this.mapToCasaClementinaCard(takenPakket.getCasaClementina()),
        this.mapToCardWithSubsections('Waar te vinden',
          [new CardContentSubSection([takenPakket.getStofzuigerWaarTeVinden()], 'Stofzuiger:'),
            new CardContentSubSection([takenPakket.getPoetsproductenWaarTeVinden()], 'Poetsproducten:'),
            new CardContentSubSection([takenPakket.getDoekenVoddenDweilenWaarTeVinden()], 'Doeken/vodden/dweilen:'),
            new CardContentSubSection([takenPakket.getTelescopischeSteelWaarTeVinden()], 'Telescopische steel:'),
            new CardContentSubSection([takenPakket.getAndereWaarTeVinden()], 'Ander poetsmateriaal:'),
          ]),
        this.mapToCard('Aandachtspunten bij het poetsen', [takenPakket.getAandachtspuntenPoetsen()]),
        this.mapToCard('Aandachtspunten bij andere taken', [takenPakket.getAandachtspuntenAndereTaken()]),
      ].filter(line => !!line);
    } else {
      return []
    }
  }

  private mapClientGerichteTakenPakket(takenPakket: Takenpakket | undefined) {
    if (takenPakket) {
      return [{text: 'Takenpakket binnen Zorg Leuven', style: 'subheader'},
        this.mapToCard('Reden van opstart', [takenPakket.getRedenOpstart()]),
        this.mapToCard('Te behalen doelstellingen', [takenPakket.getTeBehalenDoelstellingen()]),
        this.mapToTakenPakketCategorieCard('Poetsdienst', takenPakket.getPoetsdienst()),
        this.mapToTakenPakketCategorieCard('Gezinszorg', takenPakket.getGezinszorg()),
        this.mapToTakenPakketCategorieCard('Maaltijden aan huis', takenPakket.getMaaltijdenAanHuis()),
        this.mapToTakenPakketCategorieCard('Klusjesdienst', takenPakket.getKlusjesdienst()),
      ].filter(line => !!line);
    } else {
      return []
    }
  }


  private mapToTakenPakketCategorieCard(title: string, takenPakketCategorie: TakenpakketCategorie | undefined) {
    if (takenPakketCategorie) {
      return this.mapToCardWithSubsections(title,
        [new CardContentSubSection([takenPakketCategorie.getStatus()]),
          new CardContentSubSection([takenPakketCategorie.getFrequentie()]),
          new CardContentSubSection([takenPakketCategorie.getVasteMedewerkers()], 'Vaste verzorgenden:'),
          new CardContentSubSection([takenPakketCategorie.getBelangrijksteTaken()], 'Belangrijkste taken:'),
          new CardContentSubSection([takenPakketCategorie.getSpecifiekeInformatieVoorWeekend()], 'Specifieke informatie voor het weekend:'),
        ]
      );
    } else {
      return undefined;
    }
  }

  private mapToCasaClementinaCard(casaClementina: CasaClementina | undefined) {
    if (casaClementina) {
      return this.mapToCardWithSubsections('Casa Clementina',
        [new CardContentSubSection([casaClementina.getContactpersoonNoodgeval()?.getNaam(), casaClementina.getContactpersoonNoodgeval()?.getRelatieTotHulpvrager(), casaClementina.getContactpersoonNoodgeval()?.getTelefoonnummer(),], 'Contactpersoon in noodgeval:'),
          new CardContentSubSection([casaClementina.getAandachtspuntenEnGemaakteAfspraken()], 'Aandachtspunten en gemaakte afspraken:'),
          new CardContentSubSection(casaClementina.getTransportOptiesNaarCasaClementina().map(optie => '- ' + optie), 'Hoe geraakt cliënt in Casa Clementina:'),
          new CardContentSubSection([casaClementina.getVerduidelijkingAndereTransportOptieNaarCasaClementina()], 'Verduidelijking andere:'),
          new CardContentSubSection(casaClementina.getFinancieleOpties().map(optie => '- ' + optie), 'Financieel:'),
        ]
      );
    } else {
      return undefined;
    }
  }

  private mapKnelpunten(knelpunten: Knelpunten | undefined) {
    if (knelpunten) {
      return [{text: 'Knelpunten', style: 'subheader'},
        this.mapToCard('Allergieën',
          [knelpunten.getAllergieen()]
        ),
        this.mapToCard('Huisdieren',
          [knelpunten.getHuisdieren()]
        ),
        this.mapToCard('Roken',
          [knelpunten.getRoken()]
        ),
      ].filter(line => !!line);
    } else {
      return []
    }

  }

  private mapMogelijkeProblematieken(mogelijkeProblematieken: MogelijkeProblematieken | undefined) {
    if (mogelijkeProblematieken) {
      return [{text: 'Mogelijke problematieken', style: 'subheader'},
        this.mapToCard('Fysiek',
          [mogelijkeProblematieken.getFysiek()]
        ),
        this.mapToCard('Mentaal',
          [mogelijkeProblematieken.getMentaal()]
        ),
        this.mapToCard('Indien psy-cliënt: handvaten om met deze cliënt om te gaan',
          [mogelijkeProblematieken.getHandvatenOmMetClientOmTeGaan()]
        ),
        this.mapToCard('Financieel',
          [mogelijkeProblematieken.getFinancieel()]
        ),
        this.mapToCard('Andere problematieken',
          [mogelijkeProblematieken.getAndere()]
        ),
        this.mapToCard('Afsprakennota – korte samenvatting',
          [mogelijkeProblematieken.getAfsprakennota()]
        ),
      ].filter(line => !!line);
    } else {
      return []
    }

  }

  private mapAndereDiensten(diensten: Array<ClientFicheDienst>) {
    if (diensten.length > 0) {
      return [{text: 'Andere diensten', style: 'subheader'},
        ...(diensten.map(dienst => this.mapToDienstCard(dienst))),
      ]
    } else {
      return []
    }
  }

  private mapToDienstCard(dienst: ClientFicheDienst) {
    const funtie = dienst.getFunctie() ? dienst.getFunctie() : 'functie niet ingevuld ';
    const naamDienstverlenerOfOrganisatie = dienst.getNaamDienstverlenerOfOrganisatie() ? dienst.getNaamDienstverlenerOfOrganisatie() : 'naam niet ingevuld';
    return {
      stack:
        [{
          text: funtie + ' - ' + naamDienstverlenerOfOrganisatie,
          style: 'cardheader'
        },
          this.mapToCardContent(dienst.getExtraInfo()),
        ]
    }
  }

  private mapToCard(title: string, content: Array<string>) {
    const actualCardContent = content.filter(contentLine => !!contentLine);
    if (actualCardContent.length > 0) {
      return {
        stack:
          [{text: title, style: 'cardheader'},
            ...actualCardContent.map(contentLine => this.mapToCardContent(contentLine)),
          ]
      };
    } else {
      return undefined
    }
  }


  private mapToCardWithSubsections(title: string, content: Array<CardContentSubSection>) {
    const actualCardContentSubSections = content.filter(contentSubSection => !!contentSubSection && contentSubSection.hasContent());
    if (actualCardContentSubSections.length > 0) {
      return {
        stack:
          [{text: title, style: 'cardheader'},
            ...actualCardContentSubSections
              .flatMap((contentSubSection, index) => {
                const isFirstSubSection = index == 0;
                return this.mapToContentSubSection(contentSubSection, isFirstSubSection);
              }),
          ]
      };
    } else {
      return undefined
    }
  }

  private mapToCardContent(content: string) {
    return content ? {text: content, style: 'cardcontent'} : {};
  }

  private mapToContentSubSection(contentSubSection: CardContentSubSection, isFirstSubSection: boolean) {
    const subsectionHeader =
      contentSubSection.hasHeader()
        ? {
          text: contentSubSection.getHeader(),
          style: isFirstSubSection ? 'cardcontentsubheader' : ['cardcontentsubheader', 'margintop10']
        }
        : undefined;
    const subsectionsContentLines = contentSubSection.getContent().map(contentLine => this.mapToCardContent(contentLine));
    return [subsectionHeader, ...subsectionsContentLines].filter(line => !!line);
  }
}

class CardContentSubSection {
  constructor(
    private content: Array<string>,
    private header?: string,
  ) {
  }

  public hasContent(): boolean {
    return this.content.filter(contentLine => !!contentLine).length > 0
  }

  public getContent(): Array<string> {
    return this.content
  }

  public hasHeader(): boolean {
    return !!this.header;
  }

  public getHeader(): string | undefined {
    return this.header
  }
}
